import type { ScoreConflict, ScoreDoc } from '../types';

type EntityName = ScoreConflict['entity'];
type MergeResult = { score: ScoreDoc; conflicts: ScoreConflict[] };

const clone = <T,>(value: T): T => structuredClone(value);
const same = (left: unknown, right: unknown) => JSON.stringify(left) === JSON.stringify(right);

function mergeEntity<T extends { id: string }>(
  entity: EntityName,
  scoreId: string,
  baseValue: T | undefined,
  localValue: T | undefined,
  remoteValue: T | undefined,
  conflicts: ScoreConflict[],
  label: (field: string) => string,
): T | undefined {
  if (!baseValue) {
    if (!localValue) return remoteValue && clone(remoteValue);
    if (!remoteValue) return clone(localValue);
    if (same(localValue, remoteValue)) return clone(localValue);
    const keys = new Set([...Object.keys(localValue), ...Object.keys(remoteValue)]);
    const result = clone(remoteValue) as Record<string, unknown>;
    for (const field of keys) {
      const left = (localValue as Record<string, unknown>)[field];
      const right = (remoteValue as Record<string, unknown>)[field];
      if (same(left, right)) result[field] = clone(left);
      else if (field !== 'id') {
        conflicts.push({ id: `${entity}:${localValue.id}:${field}`, scoreId, entity, entityId: localValue.id, field, label: label(field), localValue: clone(left), remoteValue: clone(right) });
        result[field] = clone(left);
      }
    }
    return result as T;
  }

  if (!localValue && !remoteValue) return undefined;
  if (!localValue && remoteValue) {
    if (same(baseValue, remoteValue)) return undefined;
    conflicts.push({ id: `${entity}:${baseValue.id}:$delete`, scoreId, entity, entityId: baseValue.id, field: '$delete', label: `${label('delete')}：${baseValue.id}`, localValue: null, remoteValue: clone(remoteValue) });
    return clone(remoteValue);
  }
  if (localValue && !remoteValue) {
    if (same(baseValue, localValue)) return undefined;
    conflicts.push({ id: `${entity}:${baseValue.id}:$delete`, scoreId, entity, entityId: baseValue.id, field: '$delete', label: `${label('delete')}：${baseValue.id}`, localValue: clone(localValue), remoteValue: null });
    return clone(localValue);
  }

  const baseRecord = baseValue as Record<string, unknown>;
  const localRecord = localValue as Record<string, unknown>;
  const remoteRecord = remoteValue as Record<string, unknown>;
  const result: Record<string, unknown> = {};
  for (const field of new Set([...Object.keys(baseRecord), ...Object.keys(localRecord), ...Object.keys(remoteRecord)])) {
    const before = baseRecord[field];
    const left = localRecord[field];
    const right = remoteRecord[field];
    if (same(left, before)) result[field] = clone(right);
    else if (same(right, before) || same(left, right)) result[field] = clone(left);
    else {
      conflicts.push({ id: `${entity}:${baseValue.id}:${field}`, scoreId, entity, entityId: baseValue.id, field, label: label(field), localValue: clone(left), remoteValue: clone(right) });
      result[field] = clone(left);
    }
  }
  return result as T;
}

function mergeList<T extends { id: string }>(entity: EntityName, scoreId: string, base: T[], local: T[], remote: T[], conflicts: ScoreConflict[], label: (field: string) => string) {
  const baseMap = new Map(base.map((item) => [item.id, item]));
  const localMap = new Map(local.map((item) => [item.id, item]));
  const remoteMap = new Map(remote.map((item) => [item.id, item]));
  const ids = new Set([...baseMap.keys(), ...localMap.keys(), ...remoteMap.keys()]);
  return [...ids].flatMap((id) => {
    const merged = mergeEntity(entity, scoreId, baseMap.get(id), localMap.get(id), remoteMap.get(id), conflicts, label);
    return merged ? [merged] : [];
  });
}

const fieldLabel = (field: string) => ({
  chord: '和弦', degree: '級數', lyric: '歌詞', lyricX: '歌詞水平位置', lyricY: '歌詞垂直位置', section: '段落', text: '文字', x: '水平位置', y: '垂直位置',
  notes: '音符', drum: '鼓譜', name: '名稱', color: '顏色', visible: '顯示狀態', title: '歌名', key: '調性', bpm: '速度',
  mode: '調式', fontSize: '字體大小', bold: '粗體', assetUrl: '圖片', sourceDataUrl: '原譜檔案',
} as Record<string, string>)[field] ?? field;

export function mergeScores(base: ScoreDoc, local: ScoreDoc, remote: ScoreDoc): MergeResult {
  const conflicts: ScoreConflict[] = [];
  const merged = clone(remote);
  const scalarKeys: Array<keyof ScoreDoc> = ['title', 'key', 'mode', 'bpm', 'timeSignature', 'barsPerRow', 'rowsPerPage', 'density', 'viewMode', 'pageMode', 'sourceDataUrl', 'sourceAssetKey', 'sourceMimeType', 'sourceName', 'sourceIsPdf', 'sourceAspectRatio', 'beatTimes', 'audioDuration', 'sharedWith'];
  for (const key of scalarKeys) {
    const before = base[key];
    const left = local[key];
    const right = remote[key];
    if (same(left, before)) (merged as unknown as Record<string, unknown>)[key] = clone(right);
    else if (same(right, before) || same(left, right)) (merged as unknown as Record<string, unknown>)[key] = clone(left);
    else {
      conflicts.push({ id: `score:${local.id}:${String(key)}`, scoreId: local.id, entity: 'score', entityId: local.id, field: String(key), label: fieldLabel(String(key)), localValue: clone(left), remoteValue: clone(right) });
      (merged as unknown as Record<string, unknown>)[key] = clone(left);
    }
  }
  merged.bars = mergeList('bar', local.id, base.bars ?? [], local.bars ?? [], remote.bars ?? [], conflicts, fieldLabel);
  merged.objects = mergeList('object', local.id, base.objects ?? [], local.objects ?? [], remote.objects ?? [], conflicts, fieldLabel);
  merged.parts = mergeList('part', local.id, base.parts ?? [], local.parts ?? [], remote.parts ?? [], conflicts, fieldLabel);
  merged.layers = mergeList('layer', local.id, base.layers ?? [], local.layers ?? [], remote.layers ?? [], conflicts, fieldLabel);
  merged.sections = [...new Set([...(remote.sections ?? []), ...(local.sections ?? [])])];
  merged.updatedAt = Math.max(local.updatedAt, remote.updatedAt, Date.now());
  return { score: merged, conflicts };
}

export function resolveConflict(score: ScoreDoc, conflict: ScoreConflict, use: 'local' | 'remote'): ScoreDoc {
  const value = use === 'local' ? conflict.localValue : conflict.remoteValue;
  const next = clone(score);
  if (conflict.entity === 'score') {
    (next as unknown as Record<string, unknown>)[conflict.field] = clone(value);
  } else {
    const listKey = conflict.entity === 'bar' ? 'bars' : conflict.entity === 'object' ? 'objects' : conflict.entity === 'part' ? 'parts' : 'layers';
    const list = next[listKey] as Array<{ id: string }>;
    const index = list.findIndex((item) => item.id === conflict.entityId);
    if (conflict.field === '$delete') {
      if (value === null) list.splice(index, 1);
      else if (index >= 0) list[index] = clone(value as { id: string });
      else list.push(clone(value as { id: string }));
    } else if (index >= 0) {
      (list[index] as unknown as Record<string, unknown>)[conflict.field] = clone(value);
    }
  }
  next.updatedAt = Date.now();
  return next;
}
